<template>
  <div>
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :drop-menu="menu"
      :report-title="pageTitle"
      :range-value="toolbarRange"
      :toolbar-disabled="isStatic || isDisabled"
      @onSelectDropdown="onChangeCalculationMode"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
    >
      <template #body>
        <vertical-bar-chart
          :config="{}"
          :stub-config="stubConfig"
          :is-static="true"
          :custom-legends="true"
          :show-stub-message="false"
        />
      </template>
    </skeleton-wrapper>
    <vertical-bar-chart
      v-else
      id="report_export_id"
      ref="chart"
      :config="chartConfig"
      :stub-config="stubConfig"
      :is-static="isStatic"
      :custom-legends="true"
      @onAllLegendsDisabled="flag => isDisabled = flag"
    />
  </div>
</template>

<script>
import app from '$$app';
import reportsTitle from './title.vue';
import reportsToolbar from './toolbar.vue';
import verticalBarChart from './verticalBarChart.vue';
import constants from '$$helpers/constants';
import multiViewsProjects from '$$multiviewModel';
import timeReportConfigs from '../time/chartsConfig';
import budgetReportConfigs from '../budget/chartsConfig';
import ProjectsTimeReport from '../time/projectsTimeReport';
import ProjectsBudgetReport from '../budget/projectsBudgetReport';
import moment from '../../../libs/moment';
import reportsConstants from './constants';
import projectsModel from '../../../models/projects';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';

const MONTH_LOCALES = [
  __('report_month_jan'),
  __('report_month_feb'),
  __('report_month_march'),
  __('report_month_apr'),
  __('report_month_may'),
  __('report_month_jun'),
  __('report_month_jul'),
  __('report_month_aug'),
  __('report_month_sep'),
  __('report_month_oct'),
  __('report_month_nov'),
  __('report_month_dec'),
];

export default {
  components: {
    reportsTitle, reportsToolbar, verticalBarChart, skeletonWrapper,
  },
  props: {
    reportType: { type: String, required: true, default: 'budget' },
  },
  data() {
    return {
      appEvents: [],
      locale: __,
      projectIds: [],
      multiviewProjectIds: [],
      baselines: [],
      baselineData: {},
      baselineId: null,
      chartConfig: null,
      resultConfig: null,
      clientWidth: 0,
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      toolbarRange: '',
      isStatic: true,
      isShowSkeleton: true,
      isDisabled: false,
    };
  },
  computed: {
    pageTitle() {
      if (this.$route?.name === 'timeReports') {
        return reportsConstants.TITLE.time.timeByProjects;
      }
      if (this.$route?.name === 'budgetReports') {
        return reportsConstants.TITLE.budget.budgetByProjects;
      }

      return '';
    },
    menu() {
      return [
        {
          type: 'item',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}`),
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
          isDisabled: this.isStatic,
          group: __('report_calculation_group_actual_label'),
          tooltip: { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED}_multiple_report`), position: 'right' },
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE ? this.baselineId : null,
          group: __('report_calculation_group_baseline_label'),
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE}_multiple_report`), position: 'right' } : null,
        },
        {
          type: 'submenu',
          name: constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          text: __(`overview_type_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}`),
          list: this.baselines,
          isActive: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE,
          isDisabled: !this.baselines.length || this.isStatic,
          selectedSubmenuId: this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE ? this.baselineId : null,
          tooltip: !this.baselines.length ? { content: __(`reports_pages_calculation_model_tooltip_${constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE}_multiple_report`), position: 'right' } : null,
        },
      ];
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    baselineTasks() {
      if (this.baselineData?.tasks) {
        const baselineTasks = this.baselineData.tasks;

        return baselineTasks;
      }

      return [];
    },
    reportChartConfigs() {
      let configs = {};

      if (this.reportType === 'budget') {
        configs = { ...budgetReportConfigs.projectsReport() };
      }

      if (this.reportType === 'time') {
        configs = { ...timeReportConfigs.projectsReport() };
      }

      return configs;
    },
    stubConfig() {
      return this.reportChartConfigs.stub;
    },
    filterProjectIds() {
      return [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];
    },
    currentProjectIds() {
      if (!this.filterProjectIds.length) {
        return projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      return this.filterProjectIds;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
  },
  watch: {
    'currentFilter.properties.gantt_id': function (projectIds) {
      if (projectIds?.length) {
        this.projectIds = [...projectIds];
      } else {
        this.projectIds = [];
      }
    },
    'currentFilter.properties.portfolio_id': function (portfolioIds) {
      if (portfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(portfolioIds[0]);

        this.multiviewProjectIds = [...multiviewData.ganttIDs];
      } else {
        this.multiviewProjectIds = [];
      }
    },
    'currentFilter.properties.rangeDate': function (range) {
      this.setToolbarRangeValue();
      this.calculateReportByWorker();
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    async currentProjectIds(projectIds) {
      if (projectIds?.length) {
        if (projectIds.length > 1) {
          this.calculationMode = this.menu[0].name;
          this.updateFilterAdvancedSettings({ isShowNotificationMessage: false, baselineEnabled: false });
        }

        await this.getBaselines();
        this.calculateReportByWorker();
      } else {
        setTimeout(() => {
          this.isStatic = true;
          this.chartConfig = { ...this.stubConfig };
        }, 0);
      }
    },
    filterProjectIds(ids) {
      if (!ids.length) {
        const chartComponent = this.$refs.chart;

        if (chartComponent && chartComponent.instance) {
          chartComponent.instance.dispatchAction({
            type: 'legendAllSelect',
          });
        }
      }
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
  },
  created() {
    this.init();
  },
  beforeUnmount() {
    this.appEvents.forEach(event => app.off(event));
  },
  mounted() {
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      calculation_mode: this.calculationMode,
      source_type: this.sourceType,
      projects_selected_count: this.currentProjectIds.length,
    });

    const onAfterTimeCalculation = app.on('onAfterProjectsTimeReportCalculation', result => {
      this.setChartData(result);
    });
    const onAfterBudgetCalculation = app.on('onAfterProjectsBudgetCalculation', result => {
      this.setChartData(result);
    });

    this.setToolbarRangeValue();
    this.appEvents.push(onAfterTimeCalculation, onAfterBudgetCalculation);
    this.clientWidth = `${document.documentElement.clientWidth - this.leftSidebarWidth}px`;
  },
  methods: {
    init() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.initCostWorker();
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.initTimeWorker();
      }

      this.chartConfig = { ...this.stubConfig };
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.currentFilter?.properties?.gantt_id?.length) {
        this.projectIds = [...this.currentFilter.properties.gantt_id];
      }

      if (this.currentFilter?.properties?.portfolio_id?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter?.properties?.portfolio_id[0]);

        if (multiviewData) {
          this.multiviewProjectIds = [...multiviewData.ganttIDs];
        }
      }

      if (!this.filterProjectIds.length) {
        this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);
      }
    },

    calculateReportByWorker() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.calculateCosts(this.currentProjectIds, this.baselineTasks, this.calculationMode);
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.calculateTime(this.currentProjectIds, this.baselineTasks, this.calculationMode);
      }
    },

    recalculateChart() {
      if (this.reportType === 'budget') {
        ProjectsBudgetReport.calculationMode = this.calculationMode;
        ProjectsBudgetReport.calculateProjectsBudgetReport();
      }

      if (this.reportType === 'time') {
        ProjectsTimeReport.calculationMode = this.calculationMode;
        ProjectsTimeReport.calculateProjectsTimeLogReport();
      }
    },

    setToolbarRangeValue() {
      const range = this.currentFilter?.properties?.rangeDate;

      if (range?.start && range?.end) {
        const start = moment(range.start);
        const end = moment(range.end);

        this.toolbarRange = `${start.date()} ${MONTH_LOCALES[start.month()]} ${start.year()} 
          - ${end.date()} ${MONTH_LOCALES[end.month()]} ${end.year()}`;
      } else {
        this.toolbarRange = '';
      }
    },
    async getBaselines() {
      this.baselineId = null;
      this.baselines = [];
      this.baselineData = {};

      if (this.currentProjectIds.length === 1) {
        await this.$store.dispatch('baselines/fetchBaselines', this.currentProjectIds[0]);

        const baselines = this.$store.getters['baselines/getProjectBaselines'](this.currentProjectIds[0])
          .map(item => ({ ...item, created_at: new Date(item.created_at) }))
          .sort((a, b) => a.created_at - b.created_at);

        if (baselines.length) {
          this.baselines = baselines;
          this.baselineId = baselines[baselines.length - 1].id;
          await this.getBaselineData(this.baselineId);
          this.updateFilterAdvancedSettings({ isShowNotificationMessage: false });
        } else {
          this.baselines = [];
          this.baselineData = {};

          const calculationswithBaseline = [constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE, constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE];

          if (calculationswithBaseline.includes(this.calculationMode)) {
            this.updateFilterAdvancedSettings({ isShowNotificationMessage: true });
          }
          this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
          ProjectsBudgetReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
          ProjectsTimeReport.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
        }
      }
    },

    async getBaselineData(id) {
      const baseline = this.baselines.find(b => b.id === id);

      if (!baseline) {
        this.baselineData = {};

        return;
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = id;
      } else {
        this.baselineData = {};
      }
    },

    updateFilterAdvancedSettings(settings) {
      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        type: this.currentFilter.type,
        settings,
      });
    },

    setChartData(result) {
      if (result) {
        this.isStatic = false;
        this.chartConfig = { ...result };
        this.resultConfig = result;
      } else {
        setTimeout(() => {
          this.isStatic = true;
          this.chartConfig = { ...this.stubConfig };
        }, 0);
      }
      this.isShowSkeleton = false;
      statusHelper.handlers.hideMainSpinner();
    },

    async onChangeCalculationMode(selected) {
      statusHelper.handlers.showMainSpinner();
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;

      if (selected.baselineId) {
        await this.getBaselineData(selected.baselineId);
        this.updateFilterAdvancedSettings({ baselineEnabled: true });
        this.calculateReportByWorker();
      } else {
        this.baselineId = null;
        this.updateFilterAdvancedSettings({ baselineEnabled: false });
        this.recalculateChart();
      }
    },
  },
};
</script>
